import { useCallback, useEffect, useState } from "react";

import {
  createBoard,
  deleteBoard,
  getBoards,
  updateBoard,
} from "../services/boardsService";

import type { Board } from "../services/boardsService";

type SaveNewBoardParams = {
  title: string;
  excalidrawData: unknown;
};

type UpdateExistingBoardParams = {
  boardId: string;
  title?: string;
  excalidrawData: unknown;
};

export const useBoards = (userId: string) => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshBoards = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextBoards = await getBoards(userId);
      setBoards(nextBoards);
      return nextBoards;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Nao foi possivel carregar boards.";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refreshBoards().catch((err) => {
      console.error("[boards] failed to load boards", err);
    });
  }, [refreshBoards]);

  const saveNewBoard = useCallback(
    async ({ title, excalidrawData }: SaveNewBoardParams) => {
      setError(null);

      try {
        const board = await createBoard({
          userId,
          title,
          excalidrawData,
        });
        await refreshBoards();
        return board;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Nao foi possivel salvar board.";
        setError(message);
        throw err;
      }
    },
    [refreshBoards, userId],
  );

  const updateExistingBoard = useCallback(
    async ({ boardId, title, excalidrawData }: UpdateExistingBoardParams) => {
      setError(null);

      try {
        const board = await updateBoard({
          boardId,
          userId,
          title,
          excalidrawData,
        });
        await refreshBoards();
        return board;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Nao foi possivel atualizar board.";
        setError(message);
        throw err;
      }
    },
    [refreshBoards, userId],
  );

  const archiveBoard = useCallback(
    async (boardId: string) => {
      setError(null);

      try {
        await deleteBoard(boardId, userId);
        await refreshBoards();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Nao foi possivel arquivar board.";
        setError(message);
        throw err;
      }
    },
    [refreshBoards, userId],
  );

  return {
    boards,
    loading,
    error,
    refreshBoards,
    saveNewBoard,
    updateExistingBoard,
    archiveBoard,
  };
};
